import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { setSelectedFiTypes } from "../features/account/accountSlice";
import { fetchFiCategoriesThunk } from "../features/fip/fipThunk";
import { setSelectedCategory } from "../features/fip/fipSlice";
import Button from "../components/ui/Button";

const SelectAccountType: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { categories, loading } = useAppSelector((state) => state.fip);

    // Default to BANK internally if we want, but better to start empty
    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

    useEffect(() => {
        dispatch(fetchFiCategoriesThunk());
    }, [dispatch]);

    // Filter available types dynamically from the API where STATUS is 'Y'
    const availableTypes = categories?.filter(c => c.STATUS === "Y") || [];

    const toggleType = (id: string) => {
        setSelectedTypes(prev =>
            prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
        );
    };

    const handleProceed = () => {
        // Store selected types in Redux for the discovery process
        dispatch(setSelectedFiTypes(selectedTypes));
        // Pass the primary selected type to FIP search logic
        if (selectedTypes.length > 0) {
            dispatch(setSelectedCategory(selectedTypes[0]));
        }
        navigate("/discoveryLoader");
    };

    return (
        <div className="flex flex-col min-h-screen bg-fin-bg">
            {/* Main Content */}
            <div className="flex-1 flex flex-col p-6 w-full max-w-sm mx-auto mt-6">

                {/* Link Icon */}
                <div className="h-14 w-14 bg-fin-primary/10 rounded-full flex items-center justify-center mb-6 border border-fin-primary/20 mx-auto">
                    <svg className="w-7 h-7 text-fin-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                </div>

                <div className="text-center mb-8 w-full">
                    <h2 className="text-xl font-bold text-white leading-snug">
                        Select account types to link
                    </h2>
                    <p className="text-sm text-fin-slate mt-2">
                        Choose the types of financial assets you wish to securely link to this service.
                    </p>
                </div>

                {/* Account Types List */}
                <div className="space-y-3 w-full mb-8 relative">
                    {loading && availableTypes.length === 0 && (
                        <div className="flex justify-center p-6">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-fin-primary"></div>
                        </div>
                    )}
                    {availableTypes.map(type => {
                        const isSelected = selectedTypes.includes(type.ID);
                        return (
                            <div
                                key={type.ID}
                                onClick={() => toggleType(type.ID)}
                                className={`flex items-start p-4 rounded-xl border transition-all cursor-pointer ${isSelected
                                    ? "bg-fin-primary/5 border-fin-primary shadow-none"
                                    : "bg-fin-surface border-fin-border hover:border-fin-primary/30"
                                    }`}
                            >
                                <div className="mr-4 flex-shrink-0 flex items-center justify-center">
                                    {type.LOGO ? (
                                        <div className="w-10 h-10 bg-fin-bg rounded-lg border border-fin-border p-1.5">
                                            <img
                                                src={`https://uat.camsfinserv.com/newuat/assets${type.LOGO}`}
                                                alt={type.DISPLAY_NAME}
                                                className="w-full h-full object-contain filter invert opacity-80"
                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                            />
                                        </div>
                                    ) : (
                                        <div className="w-10 h-10 bg-fin-bg rounded-lg border border-fin-border flex items-center justify-center">
                                            <svg className="w-5 h-5 text-fin-slate" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <h3 className={`font-semibold text-sm ${isSelected ? 'text-white' : 'text-fin-slate'}`}>
                                        {type.DISPLAY_NAME}
                                    </h3>
                                    <p className={`text-xs mt-1 leading-relaxed ${isSelected ? 'text-fin-primary' : 'text-fin-slate/60'}`}>
                                        Link to discover your {type.DISPLAY_NAME.toLowerCase()}
                                    </p>
                                </div>
                                <div className="ml-4 flex-shrink-0 mt-1">
                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected
                                        ? "border-fin-primary bg-fin-primary"
                                        : "border-fin-border bg-fin-bg"
                                        }`}>
                                        {isSelected && (
                                            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Sticky Footer */}
            <div className="w-full bg-fin-surface border-t border-fin-border p-4 pb-6 mt-auto">
                <div className="max-w-sm mx-auto">
                    <Button
                        variant="primary"
                        fullWidth
                        onClick={handleProceed}
                        disabled={selectedTypes.length === 0}
                        className="py-3.5 text-[15px] font-bold"
                    >
                        Proceed
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default SelectAccountType;
